import { useMemo, useState } from 'react';
import { Alert, Empty, Modal, Skeleton, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { useResponsiveModalWidth } from '@shared/hooks/useResponsiveModalWidth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { articlesApi } from '@api/articles';
import { NormalizedApiError } from '@api/errors';
import { ErrorCode } from '@shared/constants/errorCodes';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { SortableList } from '@shared/components/SortableList';
import { recordAuditAction } from '@features/audit/recordAction';
import type { ArticleListItem, ReorderFeaturedItem } from '../types';

interface FeaturedReorderModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Featured ordering drag-and-drop modal.
 *
 * Builds the BE `featured/reorder` payload from the current order. The BE
 * supports optimistic concurrency via `rowVersion` but does not surface
 * `rowVersion` on the public list DTO yet, so we omit it on the happy path
 * and let the server skip the concurrency check. If the BE later starts
 * sending `rowVersion`, this component will forward it transparently because
 * the field is read off the raw item.
 */
export function FeaturedReorderModal({ open, onClose }: FeaturedReorderModalProps) {
  const { t } = useTranslation(['articles', 'common', 'validation', 'errors']);
  const qc = useQueryClient();
  const modalWidth = useResponsiveModalWidth(640);

  const { data, isFetching } = useQuery({
    queryKey: ['articles-featured'],
    queryFn: () =>
      articlesApi.list({
        featuredOnly: true,
        page: 1,
        pageSize: 50,
        sortBy: 'featuredOrder',
        descending: false,
      }),
    enabled: open,
  });

  const fetched = useMemo<ArticleListItem[]>(() => {
    const items = data?.items ?? [];
    // Make sure they appear in ascending featuredOrder.
    return [...items].sort((a, b) => {
      const ao = a.featuredOrder ?? Number.MAX_SAFE_INTEGER;
      const bo = b.featuredOrder ?? Number.MAX_SAFE_INTEGER;
      return ao - bo;
    });
  }, [data]);

  // Local override of the fetched order. We pair it with a `baseKey` so
  // that whenever a fresh server snapshot lands (or the modal re-opens)
  // the override is implicitly invalidated without firing a setState
  // inside an effect.
  const baseKey = `${open ? 1 : 0}:${fetched.map((it) => it.id).join(',')}`;
  const [override, setOverride] = useState<{ key: string; ids: string[] } | null>(
    null,
  );
  const liveOverride =
    override && override.key === baseKey ? override.ids : null;

  const orderedItems = useMemo<ArticleListItem[]>(() => {
    const ids = liveOverride ?? fetched.map((it) => it.id);
    const map = new Map(fetched.map((it) => [it.id, it]));
    return ids
      .map((id) => map.get(id))
      .filter((it): it is ArticleListItem => Boolean(it));
  }, [liveOverride, fetched]);

  const reorderMutation = useMutation({
    mutationFn: (items: ReorderFeaturedItem[]) => articlesApi.reorderFeatured(items),
    onSuccess: async () => {
      toast.success(t('toast.reorderSaved'));
      await qc.invalidateQueries({ queryKey: ['articles'] });
      await qc.invalidateQueries({ queryKey: ['articles-featured'] });
      void recordAuditAction({
        action: 'article.featured.reorder',
        resource: 'article',
        summary: t('audit.reorder', { count: orderedItems.length }),
        meta: { ids: orderedItems.map((it) => it.id) },
      });
      onClose();
    },
    onError: (err) => {
      if (err instanceof NormalizedApiError && err.code === ErrorCode.CONCURRENCY) {
        toast.error(t('toast.reorderConcurrency'));
        void qc.invalidateQueries({ queryKey: ['articles-featured'] });
        return;
      }
      toast.error(resolveApiErrorMessage(err));
    },
  });

  const handleSave = () => {
    const payload: ReorderFeaturedItem[] = orderedItems.map((it, idx) => ({
      id: it.id,
      featuredOrder: idx,
      // Forward rowVersion if the BE starts emitting it on list rows.
      rowVersion:
        (it as ArticleListItem & { rowVersion?: string | null }).rowVersion ??
        null,
    }));
    reorderMutation.mutate(payload);
  };

  return (
    <Modal
      title={t('reorder.title')}
      open={open}
      onCancel={() => onClose()}
      width={modalWidth}
      okText={t('reorder.save')}
      cancelText={t('reorder.cancel')}
      onOk={() => handleSave()}
      confirmLoading={reorderMutation.isPending}
      destroyOnHidden
    >
      <Alert
        showIcon
        type="info"
        style={{ marginBottom: 12 }}
        message={t('reorder.hint')}
      />
      {isFetching ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : orderedItems.length === 0 ? (
        <Empty
          description={t('reorder.empty')}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <SortableList<ArticleListItem>
          items={orderedItems}
          getId={(a) => a.id}
          onReorder={(next) =>
            setOverride({ key: baseKey, ids: next.map((it) => it.id) })
          }
          renderItem={(a, idx) => (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                width: '100%',
                minWidth: 0,
              }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  style={{
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span
                    style={{
                      color: 'var(--ds-text-secondary)',
                      marginRight: 6,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    #{idx + 1}
                  </span>
                  {a.title}
                </div>
                <div style={{ marginTop: 4 }}>
                  {a.categoryId ? (
                    <Tag style={{ fontSize: 11 }}>{a.categoryId.slice(0, 8)}…</Tag>
                  ) : null}
                </div>
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--ds-text-secondary)',
                  fontVariantNumeric: 'tabular-nums',
                  whiteSpace: 'nowrap',
                }}
              >
                {t('reorder.views', { views: a.viewCount.toLocaleString('tr-TR') })}
              </div>
            </div>
          )}
        />
      )}
    </Modal>
  );
}
